import { useTranslation } from 'react-i18next';
import styleVariables from '@/styles/style.module.scss';
import { useAssociationState } from './provider/context';
import { Box, Typography, List, ListItem, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { isEmpty } from 'lodash';
import MultiProfileDialog from './MultiProfileDialog';
import BaseFontIcon from '../ui/BaseFontIcon';
import BaseAlert from '../feedback/BaseAlert';
import { ASSOCIATE_PAIRING_MODE } from '@/types/pages/storeManagement/device/associate';
import { hasAvailableLayoutsForPairingMode } from '@/utils/associateUtils';
import { ChangeEvent, useMemo } from 'react';

const SINGLE_ROW_HEIGHT = 48;

const getBorderColor = (args: { isFinish: boolean; isDisabled: boolean }) => {
    const { isFinish, isDisabled } = args;

    if (isDisabled) return 'rgba(0,0,0,0.25)';
    if (isFinish) return styleVariables.successColor;
    return styleVariables.errorColor;
};

const canProfileEnterAssociateFlow = (
    subLayouts: Record<string, object[]>,
    selectedPairingMode: ASSOCIATE_PAIRING_MODE,
) => {
    if (isEmpty(subLayouts)) return false;

    // A profile can contain multiple style buckets. Flatten first, then evaluate
    // whether at least one layout matches the current pairing mode rule.
    const allLayoutsUnderProfile = Object.values(subLayouts).flat();
    return hasAvailableLayoutsForPairingMode(allLayoutsUnderProfile, selectedPairingMode);
};

const MultiAssociationContent = () => {
    const { t } = useTranslation('common');
    const { state, dispatch } = useAssociationState();
    const { profiles, dialogs, multiAssociateTemp, currentProfile, selectedPairingMode, campaignEnable } = state;

    const handlePairingModeChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextMode = event.target.value as ASSOCIATE_PAIRING_MODE;
        if (nextMode === selectedPairingMode) return;
        dispatch({
            type: 'SET_MULTI_PAIRING_MODE',
            payload: nextMode,
        });
    };

    const profileRows = useMemo(
        () =>
            Object.entries(profiles).map(([id, profile]) => {
                const isProfileAvailable = canProfileEnterAssociateFlow(profile.subLayouts, selectedPairingMode);
                const isDisabled = !isProfileAvailable;
                const isFinish = multiAssociateTemp[id]?.isFinish || false;
                const borderColor = getBorderColor({ isFinish, isDisabled });

                return {
                    id,
                    profile,
                    isDisabled,
                    isFinish,
                    borderColor,
                };
            }),
        [profiles, multiAssociateTemp, selectedPairingMode],
    );

    return (
        <>
            <Box sx={{ width: '100%' }}>
                <Typography fontWeight={700} mb={0.5}>
                    {t('Pairing Mode')}
                </Typography>
                <FormControl sx={{ mb: 1.5 }}>
                    <RadioGroup row value={selectedPairingMode} onChange={handlePairingModeChange}>
                        <FormControlLabel
                            value={ASSOCIATE_PAIRING_MODE.NORMAL}
                            control={<Radio size="small" />}
                            label={t('Normal')}
                        />
                        {campaignEnable ? (
                            <FormControlLabel
                                value={ASSOCIATE_PAIRING_MODE.IMAGE}
                                control={<Radio size="small" />}
                                label={t('Image')}
                            />
                        ) : null}
                    </RadioGroup>
                </FormControl>

                <Typography fontWeight={700} mb={0.5}>
                    {t('Profiles')}
                </Typography>
                {isEmpty(profiles) ? (
                    <BaseAlert
                        message={'These devices have no available profiles.'}
                        severity="info"
                        style={{ marginBottom: '1rem' }}
                    />
                ) : (
                    <Box
                        sx={{
                            borderRadius: 1,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            maxHeight: 450,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            p: 1.5,
                        }}
                    >
                        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {profileRows.map(({ id, profile, isDisabled, isFinish, borderColor }) => {
                                return (
                                    <ListItem
                                        key={id}
                                        disableGutters
                                        role={!isDisabled ? 'button' : undefined}
                                        tabIndex={!isDisabled ? 0 : -1}
                                        onClick={() => {
                                            if (isDisabled) return;
                                            dispatch({ type: 'SET_MULTI_CURRENT_PROFILE', payload: profile });
                                        }}
                                        sx={{
                                            height: SINGLE_ROW_HEIGHT,
                                            minHeight: SINGLE_ROW_HEIGHT,
                                            px: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            borderRadius: 1,
                                            border: `2px solid ${borderColor}`,
                                            backgroundColor: '#fff',
                                            opacity: isDisabled ? 0.65 : 1,
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            userSelect: 'none',

                                            ...(isDisabled
                                                ? null
                                                : {
                                                      '&:hover': {
                                                          backgroundColor: (theme) => theme.palette.action.hover,
                                                      },
                                                      '&:active': {
                                                          transform: 'translateY(1px)',
                                                      },
                                                      '&:focus-visible': {
                                                          outline: '2px solid rgba(25,118,210,0.35)',
                                                          outlineOffset: 2,
                                                      },
                                                  }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                minWidth: 0,
                                                flex: 1,
                                                gap: 1.25,
                                            }}
                                        >
                                            {isFinish && (
                                                <BaseFontIcon className="fa-solid fa-circle-check" color="success" />
                                            )}
                                            {isDisabled && (
                                                <BaseFontIcon
                                                    className="fa-regular fa-circle-exclamation"
                                                    color="disabled"
                                                />
                                            )}
                                            {(!isFinish || !isDisabled) && <Box sx={{ width: 20, flexShrink: 0 }} />}

                                            <Typography
                                                variant="body2"
                                                noWrap
                                                title={profile.name}
                                                sx={{
                                                    fontWeight: 600,
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                color={isDisabled ? 'text.disabled' : 'text.primary'}
                                            >
                                                {profile.name}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                            {isDisabled ? (
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: 'rgba(0,0,0,0.55)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                    gap={0.5}
                                                >
                                                    {t('Not Available')}
                                                </Typography>
                                            ) : null}

                                            {!isFinish && !isDisabled && (
                                                <BaseFontIcon className="fa-solid fa-pen" color="error" />
                                            )}
                                        </Box>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                )}
            </Box>
            {currentProfile && <MultiProfileDialog open={dialogs.multiProfile} />}
        </>
    );
};

export default MultiAssociationContent;
